import React, { useState } from 'react';
import { getGenderTextColor } from '../../utils/genderColors';
import Button from '../common/Button';
import ConfirmationModal from '../common/ConfirmationModal';
import { deleteGame } from '../../services/sessionService';
import EditGameModal from './EditGameModal';

/**
 * Social Game Card Component
 * Displays a game with edit/delete buttons for players who participated
 */
const SocialGameCard = ({
  game,
  currentUserId,
  playersById,
  onGameUpdated,
  onGameDeleted,
  scoringConfig,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get players in the game
  const getTeamPlayers = (teamNumber) => {
    if (!game.players_session_games) return [];
    return game.players_session_games
      .filter(psg => psg.team_number === teamNumber)
      .map(psg => psg.players)
      .filter(Boolean);
  };

  const team1Players = getTeamPlayers(1);
  const team2Players = getTeamPlayers(2);

  // Check if current user participated in this game
  const userParticipated = game.players_session_games?.some(
    psg => psg.player_id === currentUserId
  ) || false;

  const isTeam1Winner = game.winning_team === 1;
  const isTeam2Winner = game.winning_team === 2;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteGame(game.id);
      if (onGameDeleted) {
        onGameDeleted(game.id);
      }
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting game:', error);
      alert('Failed to delete game: ' + (error.message || 'Unknown error'));
    } finally {
      setIsDeleting(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  return (
    <>
      <div className="bg-surface rounded-lg border border-border-medium shadow-sm p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-text-secondary">
                {game.game_types?.name || 'Unknown Game Type'}
              </span>
              <span className="text-xs text-text-muted">
                {formatDate(game.created_at)}
              </span>
            </div>
            <div className="text-xs text-text-muted">
              {formatTime(game.created_at)}
            </div>
          </div>
          
          {userParticipated && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="small"
                onClick={() => setShowEditModal(true)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={() => setShowDeleteModal(true)}
                className="text-danger hover:text-danger-dark"
              >
                Delete
              </Button>
            </div>
          )}
        </div>

        {/* Teams and Score */}
        <div className="flex items-center gap-4 mb-4">
          {/* Team 1 */}
          <div className={`flex-1 p-3 rounded-lg ${isTeam1Winner ? 'bg-success-light border-2 border-success' : 'bg-surface-subtle border border-border-medium'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-sm font-semibold ${isTeam1Winner ? 'text-success-dark' : 'text-text-secondary'}`}>
                Team 1
              </span>
              {isTeam1Winner && <span className="text-lg">🏆</span>}
            </div>
            <div className="space-y-1">
              {team1Players.map((player, idx) => (
                <div key={idx} className="text-sm">
                  <span className={getGenderTextColor(player.gender)}>
                    {player.first_name} {player.last_name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Score */}
          <div className="flex items-center gap-2 px-4">
            <div className="text-2xl font-bold text-text-primary">
              {game.team_1_score}
            </div>
            <div className="text-xs text-text-muted uppercase">vs</div>
            <div className="text-2xl font-bold text-text-primary">
              {game.team_2_score}
            </div>
          </div>

          {/* Team 2 */}
          <div className={`flex-1 p-3 rounded-lg ${isTeam2Winner ? 'bg-success-light border-2 border-success' : 'bg-surface-subtle border border-border-medium'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-sm font-semibold ${isTeam2Winner ? 'text-success-dark' : 'text-text-secondary'}`}>
                Team 2
              </span>
              {isTeam2Winner && <span className="text-lg">🏆</span>}
            </div>
            <div className="space-y-1">
              {team2Players.map((player, idx) => (
                <div key={idx} className="text-sm">
                  <span className={getGenderTextColor(player.gender)}>
                    {player.first_name} {player.last_name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Game"
        message="Are you sure you want to delete this game? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Edit Modal */}
      {showEditModal && (
        <EditGameModal
          game={game}
          playersById={playersById}
          currentUserId={currentUserId}
          scoringConfig={scoringConfig}
          onClose={() => setShowEditModal(false)}
          onGameUpdated={() => {
            setShowEditModal(false);
            if (onGameUpdated) {
              onGameUpdated();
            }
          }}
        />
      )}
    </>
  );
};

export default SocialGameCard;

